import React, { useRef, useCallback } from "react";
import { HiArrowUp, HiStop } from "react-icons/hi";
import { AssistantMode } from "@/models/conversation";
import {
  useEntityMentions,
  MentionableEntity,
} from "@/hooks/use-entity-mentions";
import MentionInput, { MentionInputHandle } from "./mention-input";
import EntityMentionPopup from "./entity-mention-popup";
import ModeSelector from "./mode-selector";
import { ChatTheme } from "./types";

// ============================================================================
// Props
// ============================================================================

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onStop?: () => void;
  isLoading?: boolean;
  mode: AssistantMode;
  onModeChange: (mode: AssistantMode) => void;
  disabled?: boolean;
  theme: ChatTheme;
  placeholder?: string;
  centered?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Complete chat input with mention support, send button, and mode selector.
 */
export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  onStop,
  isLoading = false,
  mode,
  onModeChange,
  disabled = false,
  theme,
  placeholder = "Posez une question... (@ pour mentionner)",
  centered = false,
}) => {
  const inputRef = useRef<MentionInputHandle>(null);

  // Entity mentions hook
  const {
    mentionState,
    filteredEntities,
    isLoading: mentionsLoading,
    handleInputChange: handleMentionInput,
    selectEntity,
    closeMention,
    setHighlightedIndex,
  } = useEntityMentions({
    includeWidgets: true,
  });

  // Handle input change (receives internal value from MentionInput)
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  // Handle mention detection (receives display value and cursor position)
  const handleMentionDetect = useCallback(
    (
      displayValue: string,
      cursorPosition: number,
      element: HTMLTextAreaElement,
    ) => {
      handleMentionInput(displayValue, cursorPosition, element);
    },
    [handleMentionInput],
  );

  // Handle entity selection from mention popup
  const handleEntitySelect = useCallback(
    (entity: MentionableEntity) => {
      // Get display value and mention map from the input ref
      const displayValue = inputRef.current?.getDisplayValue() || "";
      const mentionMap = inputRef.current?.getMentionMap();

      // Calculate cursor position: position after the inserted mention + space
      // mentionState.startIndex is the position of "@", so cursor should be at:
      // startIndex + @name.length + 1 (for the space added after mention)
      const cursorPosition =
        mentionState.startIndex + 1 + entity.name.length + 1;

      // selectEntity now works with displayValue and returns internalValue
      const newInternalValue = selectEntity(entity, displayValue, mentionMap);
      onChange(newInternalValue);

      // Focus back on input and move cursor after the inserted mention
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 0);
    },
    [selectEntity, onChange, mentionState.startIndex],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // When mention popup is open, let it handle navigation and selection
    // The popup has its own document-level keydown listener that manages everything
    if (mentionState.isOpen) {
      // Only prevent sending message while popup is open
      if (
        e.key === "Enter" ||
        e.key === "Tab" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "Escape"
      ) {
        return; // Let the popup's document listener handle it
      }
    }

    // Normal Enter to send message (Shift+Enter for new line)
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className={centered ? "w-full" : "px-4 pb-4 pt-2"}>
      <div
        className={`
          relative flex flex-col rounded-2xl px-4 py-3
          bg-white/[0.04] border border-white/[0.08]
          focus-within:border-accent/40 focus-within:ring-1 focus-within:ring-accent/10
          transition-all duration-200
        `}
      >
        <div className="flex items-start gap-3">
          <MentionInput
            ref={inputRef}
            value={value}
            onChange={handleInputChange}
            onMentionDetect={handleMentionDetect}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            theme={theme}
            minRows={1}
            maxRows={6}
          />
          {isLoading && onStop ? (
            <button
              onClick={onStop}
              className={`
                flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0
                transition-all duration-200
                bg-red-500/80 text-white hover:bg-red-500 shadow-sm shadow-red-500/20
              `}
              aria-label="Arrêter"
            >
              <HiStop className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={onSend}
              disabled={!value.trim() || disabled}
              className={`
                flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0
                transition-all duration-200
                ${
                  value.trim() && !disabled
                    ? "bg-accent text-white hover:bg-accent-dark shadow-sm shadow-accent/20"
                    : "bg-white/[0.06] text-white/30"
                }
                disabled:cursor-not-allowed
              `}
              aria-label="Envoyer"
            >
              <HiArrowUp className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex items-center mt-2.5">
          <ModeSelector
            mode={mode}
            onModeChange={onModeChange}
            disabled={disabled}
            theme={theme}
          />
        </div>
      </div>

      {/* Entity Mention Popup */}
      <EntityMentionPopup
        mentionState={mentionState}
        entities={filteredEntities}
        isLoading={mentionsLoading}
        onSelect={handleEntitySelect}
        onClose={closeMention}
        onHighlightChange={setHighlightedIndex}
      />
    </div>
  );
};

ChatInput.displayName = "ChatInput";

export default ChatInput;
